<template>
    <FormLayout>
        <FormLayoutRow>
            <div class="d-md-flex flex-fill align-items-sm-center w-md-50 is-lockable">
                <FormLayoutLabel>{{ $tm('racecontestantdetails.firstname') }}</FormLayoutLabel>

                <div class="d-flex flex-fill">
                    <InputText
                        class="flex-fill"
                        v-model="value.Firstname"
                        ref="firstNameInput"
                        :validator="new ValidatorDef({ Required: !props.isRelayRace, Max: null, Min: null })"
                        :locked="isLocked"
                        :readonly="isLocked"
                        :tabindex="isLocked ? -1 : undefined"
                    />

                    <ButtonComp
                        class="unlocked"
                        button-type="Secondary"
                        :title="$tm('racecontestantdetails.searchparticipants')"
                        @click="showParticipantSearch"
                        icon="fa-user-magnifying-glass"
                        :tabindex="isLocked ? -1 : undefined"
                    ></ButtonComp>
                    <ButtonComp
                        v-if="isLocked"
                        class="unlocked"
                        button-type="Secondary"
                        :title="$tm('racecontestantdetails.unlock')"
                        @click="toggleLockState"
                        :icon="isLocked ? 'fa-lock-open' : 'fa-lock'"
                        :tabindex="isLocked ? -1 : undefined"
                    ></ButtonComp>
                </div>
            </div>
            <div class="d-md-flex flex-fill align-items-sm-center w-md-50 is-lockable">
                <FormLayoutLabel>{{ $tm('racecontestantdetails.surname') }}</FormLayoutLabel>

                <InputText
                    v-model="value.Surname"
                    ref="surnameInput"
                    :validator="isRelayRace ? undefined : new ValidatorDef({ Required: true, Max: null, Min: null })"
                    :locked="isLocked"
                    :readonly="isLocked"
                    :tabindex="isLocked ? -1 : undefined"
                />
            </div>
        </FormLayoutRow>
    </FormLayout>
</template>

<script setup lang="ts">
import { ButtonComp, InputText, ParticipantSearch } from '@/components';
import { FormLayout, FormLayoutLabel, FormLayoutRow } from '@/components/Form';
import { useDialog } from '@/composeables';
import { $tm } from '@/locize';
import { Participant, RaceContestantDetails } from '@/objects/generated';
import { ValidatorDef, type IEditorComponent } from '@/objects/gui';
import { computed, onMounted, ref, watch } from 'vue';
import { useUserStoreRef } from '@/stores';
import type DetailViewExpose from '@/objects/DetailViewExpose';
import { FieldAlertMessage } from '@/objects';
import { useDialogBuilder } from '@/composeables/dialog/dialogbuilder';

const { user } = useUserStoreRef();

// The value of the model
const value = defineModel<RaceContestantDetails>({ required: true });

const firstNameInput = ref<IEditorComponent | null>(null);
const surnameInput = ref<IEditorComponent | null>(null);

// Holds the original bib value
const originalBibValue = value.value.Bib;

// Indicates if the bib value has changed
const isBibChanged = ref(false);

const props = defineProps<{
    /**
     * Open the participant search dialog on load
     */
    openParticipantSearch?: boolean;

    /**
     * If it is a relay race, the firstname does not need to be filled
     */
    isRelayRace?: boolean;

    /**
     * If the inputs should be locked
     */
    locked?: boolean;
}>();

// Set the lock state and emits the lock state.
const isLocked = ref<boolean>(props.locked === true || value.value.ID > 0 || value.value.ParticipantID > 0);

watch(
    () => props.locked,
    (newVal) => {
        if (newVal) {
            isLocked.value = props.locked === true || value.value.ID > 0 || value.value.ParticipantID > 0;
        }
    },
);

/**
 * Emits the lock state
 */
const emits = defineEmits<{
    (e: 'lockstate', state: boolean): void;

    (e: 'participantselected', participant: Participant): void;
}>();

/**
 * Toggles the lock state
 */
const toggleLockState = () => {
    useDialog().YesNo({
        title: 'raceparticipantdetails.unlockconfirm',
        body: $tm('raceparticipantdetails.unlockconfirmtext', '', true),
        yesCallback: async () => {
            isLocked.value = !isLocked.value;
            emits('lockstate', isLocked.value);
            return true;
        },
        options: {
            Size: 'md',
            TextAlign: 'left',
        },
    });
};

/**
 * Try and get a registered EmitID for the participant
 */
const getEmitId = () => {
    if ((value.value.ParticipantID ?? 0) <= 0) {
        return;
    }
};

/**
 * Will update the selected participant
 * @param participant
 */
const setSelectedParticipant = (participant: Participant | null) => {
    if (participant) {
        value.value.ParticipantID = participant.ID;
        value.value.CategoryID = null;
        value.value.Firstname = participant.Firstname;
        value.value.Surname = participant.Surname;
        value.value.Address1 = participant.Address1;
        value.value.Address2 = participant.Address2;
        value.value.Gender = participant.Gender;
        value.value.City = participant.City;
        value.value.PostalNumber = participant.PostalNumber;
        value.value.Email = participant.Email;
        value.value.Phone = participant.Phone;
        value.value.NationalityCountryID = participant.NationalityCountryID;
        value.value.AddressCountryISO2 = participant.AddressCountryISO2;
        value.value.Birthday = participant.Birthday;
        value.value.EventAge = participant.EventAge;
        value.value.ParticipantIDs = participant.ParticipantIDs;

        isLocked.value = value.value.ParticipantID > 0;

        emits('lockstate', isLocked.value);
        emits('participantselected', participant);
    }
};

/**
 * Show the participant search dialog
 */
const showParticipantSearch = async () => {
    const dialogInstance = await useDialogBuilder()
        .withTitle('racecontestantdetails.selectparticipant')
        .withBodyComponent(ParticipantSearch, {
            vmodel: ref(null), // Unused, but we need to have it otherwise we get a runtime warning
            componentProps: {
                checkboxSelection: false,
                fullRowSelection: true,
                details: true,
                prefillSearchText: `${value.value.Firstname} ${value.value.Surname}`.trim(),
                onParticipantSelected: (participant: Participant | null) => {
                    setSelectedParticipant(participant);
                    dialogInstance.closeDialog();
                },
            },
        })
        .withButton({
            text: $tm('general.new'),
            buttonType: 'Primary',
            action: async () => {
                const part = Participant.FromJsValues({ Firstname: '', Surname: '' });
                part.SetEntityStateAsAddedAndResetId(0);
                setSelectedParticipant(part);
                dialogInstance.closeDialog();
                return true;
            },
        })
        .withCancelButton({
            action: () => false,
        })
        .withSelectButton({
            action: async (params) => {
                if (params == null || params.dialogBodyComponentInstanceRef == null) {
                    return false;
                }
                setSelectedParticipant(params.dialogBodyComponentInstanceRef.selectedParticipant);
                return true;
            },
        })
        .withOptions({
            Size: 'xl',
        })
        .Open();
};

// Watch the bib value for changes
watch(value.value, (newValue) => (isBibChanged.value = newValue.Bib != originalBibValue));

defineExpose<DetailViewExpose<RaceContestantDetails> & { ShowParticipantSearch: () => void }>({
    val: computed(() => value.value),
    title: computed(() => ''),
    changed: computed(() => false),
    messages: computed(() => {
        let msgs: FieldAlertMessage[] = [];
        if (firstNameInput.value?.validateMessage) {
            msgs.push(
                new FieldAlertMessage(
                    'firstname',
                    firstNameInput.value!.validateMessage.message,
                    firstNameInput.value!.validateMessage.type,
                ),
            );
        }
        if (surnameInput.value?.validateMessage) {
            msgs.push(
                new FieldAlertMessage('surname', surnameInput.value!.validateMessage.message, surnameInput.value!.validateMessage.type),
            );
        }
        return msgs;
    }),
    /**
     * Show the participant search dialog
     */
    ShowParticipantSearch: showParticipantSearch,
});

// Emits the lock state
onMounted(() => {
    if (props.openParticipantSearch && (value.value?.ID ?? 0) <= 0) {
        showParticipantSearch();
    }
    emits('lockstate', isLocked.value);
});
</script>
